import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button, Field, Icon, Input, MenuButton, Modal, Notice, Panel, Select } from '../../design/index.js'
import { useApi } from '../../app/api.js'
import { navigate, paths, useRoute } from '../../app/router.js'
import { adminApi, type NewUserForm, type UserRecord } from '../../app/admin-api.js'
import { ErrorBlock, Loading, messageFor } from '../../framework/feedback.js'
import { formatDate } from '../../framework/format.js'
import { AdminList, textMatch, type AdminColumn } from './AdminList.js'
import { ChipChoice } from './pickers.js'
import { TeamBoard } from '../../framework/TeamBoard.js'
import { DataImports } from '../DataPages.js'
import './admin.css'

/**
 * Users as the reference keeps them (USR-001, USR-002, USR-004).
 *
 * A list — Name, Login Email, Role, Type, Project Permission Scope, Active —
 * with Filter and Export; Create User as one page with every required answer
 * on it; and a record page per person with a joined / not-yet-joined banner
 * and an Action menu. Each record has its own address, #/<company>/admin/users/<id>
 * (SET1-005), so it can be linked to and survives a reload.
 */

export const TYPE_WORDS: Readonly<Record<string, string>> = { ADMIN: 'Admin', NON_ADMIN: 'Non Admin', DELEGATE: 'User Delegate' }
export const SCOPE_WORDS: Readonly<Record<string, string>> = { ALL: 'All Projects', ASSIGNED: 'Assigned Projects' }
const ROW_SCOPES = [
  { value: 'ALL', label: 'All records' },
  { value: 'ASSIGNED', label: 'Assigned project records' },
  { value: 'OWN', label: 'Own records' },
]
const SIGN_IN_ROLES = [{ value: 'OWNER', label: 'Owner' }, { value: 'ADMIN', label: 'Administrator' }, { value: 'MEMBER', label: 'Member' }]

const usersKey = (companyId: string) => ['adminUserRecords', companyId]

const useBusinessRoles = () => {
  const api = useApi()
  return useQuery({
    queryKey: ['masterList', api.companyId, 'app_role'],
    queryFn: () => api.masterList('app_role', { pageSize: '200' }),
    staleTime: 60_000,
  })
}

export const PeoplePage = () => {
  const api = useApi()
  const admin = adminApi(api)
  const route = useRoute()
  const recordId = route.name === 'admin' && route.section === 'users' ? route.record ?? null : null
  const [creating, setCreating] = useState(false)
  const openRecord = (id: string | null): void => navigate(id ? `${paths.admin(api.companyId, 'users')}/${id}` : paths.admin(api.companyId, 'users'))
  const client = useQueryClient()
  const users = useQuery({ queryKey: usersKey(api.companyId), queryFn: () => admin.users() })
  const roles = useBusinessRoles()
  const [bulkOpen, setBulkOpen] = useState(false)
  const closeBulk = () => {
    setBulkOpen(false)
    void client.invalidateQueries({ queryKey: usersKey(api.companyId) })
  }

  if (creating) return <CreateUser onDone={(id) => { setCreating(false); if (id) openRecord(id) }} />
  if (recordId) return <UserRecordPage key={recordId} id={recordId} onBack={() => openRecord(null)} />

  if (users.isLoading) return <Loading />
  if (users.error) return <ErrorBlock error={users.error} />

  const roleOptions = (roles.data?.rows ?? []).map((r) => ({ value: String(r['name'] ?? ''), label: String(r['name'] ?? '') }))
  const columns: ReadonlyArray<AdminColumn<UserRecord>> = [
    { key: 'name', label: 'Name', locked: true },
    { key: 'email', label: 'Login Email', width: 240 },
    { key: 'roleName', label: 'Role', width: 180, render: (r) => r.employee?.roleName ?? '—' },
    { key: 'userType', label: 'Type', width: 130, render: (r) => TYPE_WORDS[r.userType] ?? r.userType },
    { key: 'projectScope', label: 'Project Permission Scope', width: 200, render: (r) => SCOPE_WORDS[r.projectScope] ?? r.projectScope },
    { key: 'active', label: 'Active', width: 90, render: (r) => (r.active ? 'Yes' : 'No') },
    { key: 'designation', label: 'Designation', width: 170, optional: true, render: (r) => r.employee?.designation ?? '—' },
    { key: 'lastLoginAt', label: 'Last signed in', width: 150, optional: true, render: (r) => (r.lastLoginAt ? formatDate(r.lastLoginAt.slice(0, 10)) : 'never') },
    { key: 'communicationEmail', label: 'Communication Email', width: 220, optional: true, render: (r) => r.communicationEmail ?? '—' },
    { key: 'createdAt', label: 'Created On', width: 130, optional: true, render: (r) => (r.createdAt ? formatDate(r.createdAt.slice(0, 10)) : '—') },
  ]

  return (
    <>
    <AdminList
      title="All Users"
      scope="admin:users"
      rows={users.data ?? []}
      columns={columns}
      getKey={(r) => r.id}
      onRowClick={(r) => openRecord(r.id)}
      actions={<MenuButton label="Create User" kind="primary" icon="plus" showChevron align="left" items={[
        { key: 'single', label: 'Create user', icon: 'plus', onSelect: () => setCreating(true) },
        { key: 'bulk', label: 'Bulk upload', icon: 'layers', onSelect: () => setBulkOpen(true) },
      ]} />}
      filters={[
        { key: 'name', label: 'Name', matches: (r, v) => textMatch(r.name, v) },
        { key: 'type', label: 'Type', options: Object.entries(TYPE_WORDS).map(([value, label]) => ({ value, label })), matches: (r, v) => r.userType === v },
        { key: 'role', label: 'Role', options: roleOptions, matches: (r, v) => r.employee?.roleName === v },
        { key: 'active', label: 'Active', options: [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }], matches: (r, v) => r.active === (v === 'yes') },
      ]}
      exportColumns={[
        { header: 'Name', value: (r) => r.name },
        { header: 'Login Email', value: (r) => r.email },
        { header: 'Role', value: (r) => r.employee?.roleName ?? null },
        { header: 'Type', value: (r) => TYPE_WORDS[r.userType] ?? r.userType },
        { header: 'Project Permission Scope', value: (r) => SCOPE_WORDS[r.projectScope] ?? r.projectScope },
        { header: 'Active', value: (r) => (r.active ? 'Yes' : 'No') },
      ]}
    />
    {bulkOpen ? <Modal title="Bulk upload users" wide
      subtitle="Download the template, add one person per row, check it, then import. Issue each person a password from their record when they are ready."
      onClose={closeBulk}
      actions={<Button kind="ghost" onClick={closeBulk}>Close</Button>}>
      <DataImports fixedResource="users" />
    </Modal> : null}
    </>
  )
}

/* ---- create, in one form (USR-002) ------------------------------------------ */

const BLANK: NewUserForm = {
  name: '', email: '', designation: '', userType: 'NON_ADMIN', roleId: '', companyIds: [],
  communicationEmail: '', mobile: '', landline: '', projectScope: 'ALL', projectIds: [],
  toolsScope: '', stockPurchaseScope: '', reportDownloadScope: '', expenseReportScope: '',
}

export const CreateUser = ({ onDone }: { onDone: (id: string | null) => void }) => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const [form, setForm] = useState<NewUserForm>({ ...BLANK, companyIds: [api.companyId] })
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<{ id: string; name: string; password: string } | null>(null)
  const roles = useBusinessRoles()
  const companies = useQuery({ queryKey: ['adminCompanies', api.companyId], queryFn: () => api.adminCompanies() })

  const required: Array<[keyof NewUserForm, string]> = [
    ['name', 'Name'], ['email', 'Login Email'], ['designation', 'Designation'], ['roleId', 'Role'],
    ['toolsScope', 'Tools permissions scope'], ['stockPurchaseScope', 'Permission to Stock Purchase'],
    ['reportDownloadScope', 'Report Download'], ['expenseReportScope', 'Expense Report Scope'],
  ]
  const missing = required.filter(([k]) => String(form[k] ?? '').trim() === '').map(([, l]) => l)
  if (form.companyIds.length === 0) missing.push('Allowed Companies')

  const save = useMutation({
    mutationFn: () => admin.createUser({
      ...form,
      designation: form.designation?.trim() || null,
      roleId: form.roleId || null,
      communicationEmail: form.communicationEmail?.trim() || null,
      mobile: form.mobile?.trim() || null,
      landline: form.landline?.trim() || null,
      ...(form.userType === 'DELEGATE' ? { delegateDays: form.delegateDays ?? 7 } : {}),
    }),
    onSuccess: (made) => {
      void client.invalidateQueries({ queryKey: usersKey(api.companyId) })
      setIssued({ id: made.user.id, name: made.user.name, password: made.temporaryPassword })
    },
    onError: (err) => setError(messageFor(err)),
  })

  const set = <K extends keyof NewUserForm>(k: K, v: NewUserForm[K]): void => setForm({ ...form, [k]: v })

  return (
    <>
      <button type="button" className="crumb link-button" onClick={() => onDone(null)}><Icon name="left" size={15} /> All Users</button>
      <h2 className="page-title">Create User</h2>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}

      <Panel title="General Info">
        <div className="form-grid">
          <Field label="Name" required><Input value={form.name} onChange={(v) => set('name', v)} /></Field>
          <Field label="Login Email" required hint="They sign in with this."><Input value={form.email} onChange={(v) => set('email', v)} /></Field>
          <Field label="Designation" required><Input value={form.designation ?? ''} onChange={(v) => set('designation', v)} /></Field>
          <Field label="Type" required hint="An Admin can change settings and add people.">
            <Select value={form.userType} onChange={(v) => set('userType', v as NewUserForm['userType'])}
              options={Object.entries(TYPE_WORDS).map(([value, label]) => ({ value, label }))} />
          </Field>
          <Field label="Role" required hint="What they may do comes from this role's column in Roles and permissions.">
            <Select value={form.roleId ?? ''} placeholder="Choose a role" onChange={(v) => set('roleId', v)}
              options={(roles.data?.rows ?? []).map((r) => ({ value: String(r['id']), label: String(r['name'] ?? '') }))} />
          </Field>
          {form.userType === 'DELEGATE' ? (
            <Field label="For how many days?" hint="Support access stops on its own. At most ninety.">
              <Input value={String(form.delegateDays ?? 7)} onChange={(v) => set('delegateDays', Number(v.replace(/\D/g, '')) || 7)} />
            </Field>
          ) : null}
        </div>
        <Field label="Allowed Companies" required>
          <ChipChoice label="Allowed Companies" value={form.companyIds} onChange={(v) => set('companyIds', v)}
            options={(companies.data ?? []).map((c) => ({ value: c.id, label: c.name }))} />
        </Field>
      </Panel>

      <Panel title="Contact Info">
        <div className="form-grid">
          <Field label="Communication Email" hint="Replies to what they send come here. Leave empty to use the login email.">
            <Input value={form.communicationEmail ?? ''} onChange={(v) => set('communicationEmail', v)} />
          </Field>
          <Field label="Mobile"><Input value={form.mobile ?? ''} onChange={(v) => set('mobile', v)} /></Field>
          <Field label="Landline"><Input value={form.landline ?? ''} onChange={(v) => set('landline', v)} /></Field>
        </div>
      </Panel>

      <Panel title="Data Security">
        <div className="form-grid">
          <Field label="Project Permission Scope" required>
            <Select value={form.projectScope} onChange={(v) => set('projectScope', v as 'ALL' | 'ASSIGNED')}
              options={Object.entries(SCOPE_WORDS).map(([value, label]) => ({ value, label }))} />
          </Field>
          <Field label="Tools permissions scope" required>
            <Select value={form.toolsScope} placeholder="Choose" onChange={(v) => set('toolsScope', v)} options={ROW_SCOPES} />
          </Field>
          <Field label="Permission to Stock Purchase" required>
            <Select value={form.stockPurchaseScope} placeholder="Choose" onChange={(v) => set('stockPurchaseScope', v)} options={ROW_SCOPES} />
          </Field>
          <Field label="Report Download" required hint="Own records means reports can be read but not downloaded.">
            <Select value={form.reportDownloadScope} placeholder="Choose" onChange={(v) => set('reportDownloadScope', v)} options={ROW_SCOPES} />
          </Field>
          <Field label="Expense Report Scope" required>
            <Select value={form.expenseReportScope} placeholder="Choose" onChange={(v) => set('expenseReportScope', v)} options={ROW_SCOPES} />
          </Field>
        </div>
      </Panel>

      <div className="row" style={{ margin: '20px 0' }}>
        <Button kind="ghost" onClick={() => onDone(null)}>Close</Button>
        <Button kind="primary" disabled={save.isPending || missing.length > 0} onClick={() => save.mutate()}>Create</Button>
        {missing.length > 0 ? <span className="helper">Still needed: {missing.join(', ')}</span> : null}
      </div>

      {issued ? (
        <Modal title="Their temporary password" tone="warn" onClose={() => onDone(issued.id)}
          actions={<Button kind="primary" onClick={() => onDone(issued.id)}>I have taken a note of it</Button>}>
          <p>Give this to <strong>{issued.name}</strong>. It is shown once; they choose their own when they first sign in.</p>
          <p className="temp-password num">{issued.password}</p>
        </Modal>
      ) : null}
    </>
  )
}

/* ---- one person (USR-004, SET1-005) -------------------------------------------- */

interface RecordDraft {
  name: string
  email: string
  designation: string
  roleId: string
  mobile: string
  communicationEmail: string
  landline: string
  userType: string
  projectScope: string
  toolsScope: string
  stockPurchaseScope: string
  reportDownloadScope: string
  expenseReportScope: string
  companyIds: string[]
  projectIds: string[]
}

const SCOPE_FIELDS = [
  ['toolsScope', 'Tools permissions scope'],
  ['stockPurchaseScope', 'Permission to Stock Purchase'],
  ['reportDownloadScope', 'Report Download'],
  ['expenseReportScope', 'Expense Report Scope'],
] as const

/**
 * One person, edited as one form, as in the reference: General Info holds
 * Name, Login Email, Designation, Type, Role and Allowed Companies; Contact
 * Info and Data Security follow; one Save sits in the Action menu beside Reset
 * password and Deactivate, with Close next to it and the Team Board alongside.
 */
export const UserRecordPage = ({ id, onBack }: { id: string; onBack: () => void }) => {
  const api = useApi()
  const admin = adminApi(api)
  const client = useQueryClient()
  const [error, setError] = useState<string | null>(null)
  const [issued, setIssued] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)
  const [draft, setDraft] = useState<RecordDraft | null>(null)
  const roles = useBusinessRoles()
  const user = useQuery({ queryKey: ['adminUserRecord', api.companyId, id], queryFn: () => admin.user(id) })
  const access = useQuery({ queryKey: ['userAccess', api.companyId, id], queryFn: () => api.adminUserAccess(id) })

  const refresh = (): void => {
    void client.invalidateQueries({ queryKey: ['adminUserRecord', api.companyId, id] })
    void client.invalidateQueries({ queryKey: ['userAccess', api.companyId, id] })
    void client.invalidateQueries({ queryKey: usersKey(api.companyId) })
  }
  const run = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => { refresh(); setError(null) },
    onError: (err) => setError(messageFor(err)),
  })

  if (user.isLoading || access.isLoading) return <Loading />
  if (user.error) return <ErrorBlock error={user.error} />
  const u = user.data!
  const a = access.data
  const joined = u.lastLoginAt !== null

  const stored: RecordDraft = {
    name: u.name, email: u.email,
    designation: u.employee?.designation ?? '', roleId: u.employee?.roleId ?? '', mobile: u.employee?.mobile ?? '',
    communicationEmail: u.communicationEmail ?? '', landline: u.landline ?? '',
    userType: a?.userType ?? u.userType, projectScope: a?.projectScope ?? u.projectScope,
    toolsScope: a?.toolsScope ?? '', stockPurchaseScope: a?.stockPurchaseScope ?? '',
    reportDownloadScope: a?.reportDownloadScope ?? '', expenseReportScope: a?.expenseReportScope ?? '',
    companyIds: [...(a?.companyIds ?? [])], projectIds: [...(a?.projectIds ?? [])],
  }
  const d = draft ?? stored
  const set = <K extends keyof RecordDraft>(k: K, v: RecordDraft[K]): void => { setDraft({ ...d, [k]: v }); setSaved(false) }
  const missing = (['name', 'email'] as const).filter((k) => d[k].trim() === '')

  /** The profile and the access are two calls; the one Save sends whichever changed. */
  const saveAll = async (): Promise<void> => {
    await admin.updateProfile(u.id, {
      name: d.name.trim(), email: d.email.trim(),
      designation: d.designation || null, roleId: d.roleId || null, mobile: d.mobile || null,
      communicationEmail: d.communicationEmail || null, landline: d.landline || null,
    })
    if (a) {
      await api.adminSetUserAccess(u.id, {
        userType: d.userType, projectScope: d.projectScope,
        toolsScope: d.toolsScope, stockPurchaseScope: d.stockPurchaseScope,
        reportDownloadScope: d.reportDownloadScope, expenseReportScope: d.expenseReportScope,
        companyIds: d.companyIds, projectIds: d.projectIds,
      })
    }
    setDraft(null)
    setSaved(true)
  }

  const items = [
    { key: 'save', label: 'Save', onSelect: () => {
      if (missing.length > 0) setError(`Still needed: ${missing.map((k) => (k === 'name' ? 'Name' : 'Login Email')).join(', ')}`)
      else if (draft && !run.isPending) run.mutate(saveAll)
    } },
    { key: 'reset', label: 'Reset password', onSelect: () => run.mutate(async () => setIssued((await api.adminResetPassword(u.id)).temporaryPassword)) },
    { key: 'active', label: u.active ? 'Deactivate' : 'Activate', danger: u.active, onSelect: () => run.mutate(() => api.adminSetActive(u.id, !u.active)) },
    ...SIGN_IN_ROLES.filter((r) => r.value !== u.role).map((r) => ({
      key: `role-${r.value}`, label: `Make ${r.label.toLowerCase()}`, onSelect: () => run.mutate(() => api.adminSetRole(u.id, r.value)),
    })),
    ...(u.userType === 'DELEGATE' ? [{ key: 'revoke', label: 'Revoke Access', danger: true, onSelect: () => run.mutate(() => api.adminRevokeDelegate(u.id)) }] : []),
  ]

  return (
    <>
      <div className="doc-head">
        <div>
          <h2 className="page-title">
            <button type="button" className="link-button" onClick={onBack}>User</button>
            <Icon name="right" size={15} /> {u.name} <span className="chip">{u.active ? 'Active' : 'Inactive'}</span>
          </h2>
          <p className="page-subtitle">{u.email} · Sign-in role: {SIGN_IN_ROLES.find((r) => r.value === u.role)?.label ?? u.role}</p>
        </div>
        <div className="doc-actions">
          <Button kind="ghost" onClick={onBack}>Close</Button>
          <MenuButton label="Action" kind="primary" items={items} />
        </div>
      </div>
      {error ? <div style={{ margin: '12px 0' }}><Notice kind="bad">{error}</Notice></div> : null}
      {saved ? <div style={{ margin: '12px 0' }}><Notice kind="ok">Saved.</Notice></div> : null}
      {draft ? <p className="helper">Changes are not saved yet. Use Action › Save.</p> : null}

      <div className="record-split">
        <div>
          <Panel title="General Info">
            <div className={joined ? 'status-banner' : 'status-banner status-banner--pending'} role="status">
              {joined
                ? `${u.name} has joined. He/She can log in to the application — last signed in ${formatDate(u.lastLoginAt!.slice(0, 10))}.`
                : `${u.name} has not signed in yet. Give them their temporary password, or reset it to issue a new one.`}
            </div>
            <div className="form-grid">
              <Field label="Name" required><Input value={d.name} onChange={(v) => set('name', v)} /></Field>
              <Field label="Login Email" required hint="They sign in with this."><Input value={d.email} onChange={(v) => set('email', v)} /></Field>
              <Field label="Designation" required><Input value={d.designation} onChange={(v) => set('designation', v)} /></Field>
              <Field label="Type" required>
                <Select value={d.userType} onChange={(v) => set('userType', v)}
                  options={Object.entries(TYPE_WORDS).map(([value, label]) => ({ value, label }))} />
              </Field>
              <Field label="Role" required>
                <Select value={d.roleId} placeholder="None" onChange={(v) => set('roleId', v)}
                  options={(roles.data?.rows ?? []).map((r) => ({ value: String(r['id']), label: String(r['name'] ?? '') }))} />
              </Field>
              {a ? (
                <Field label="Allowed Companies" required hint="None ticked allows every company on the account.">
                  <ChipChoice label="Allowed Companies" value={d.companyIds} onChange={(v) => set('companyIds', v)}
                    options={a.companies.map((c) => ({ value: c.id, label: c.name }))} />
                </Field>
              ) : null}
            </div>
          </Panel>
          <Panel title="Contact Info">
            <div className="form-grid">
              <Field label="Communication Email"><Input value={d.communicationEmail} onChange={(v) => set('communicationEmail', v)} /></Field>
              <Field label="Mobile"><Input value={d.mobile} onChange={(v) => set('mobile', v)} /></Field>
              <Field label="Landline"><Input value={d.landline} onChange={(v) => set('landline', v)} /></Field>
            </div>
          </Panel>

          {a ? (
            <Panel title="Data Security">
              <div className="form-grid">
                <Field label="Project Permission Scope">
                  <Select value={d.projectScope} onChange={(v) => set('projectScope', v)}
                    options={Object.entries(SCOPE_WORDS).map(([value, label]) => ({ value, label }))} />
                </Field>
                {SCOPE_FIELDS.map(([k, label]) => (
                  <Field key={k} label={label}>
                    <Select value={d[k]} onChange={(v) => set(k, v)} options={ROW_SCOPES} />
                  </Field>
                ))}
              </div>
              {d.projectScope === 'ASSIGNED' ? (
                <Field label="Assigned projects">
                  <ChipChoice label="Assigned projects" value={d.projectIds} onChange={(v) => set('projectIds', v)}
                    options={a.projects.map((p) => ({ value: p.id, label: p.name }))} />
                </Field>
              ) : null}
            </Panel>
          ) : null}
        </div>

        {/* Notes and files about this person, on their employee record. */}
        {u.employee ? <aside><TeamBoard record={{ type: 'employee', id: u.employee.id }} /></aside> : null}
      </div>

      {issued ? (
        <Modal title="Their temporary password" tone="warn" onClose={() => setIssued(null)}
          actions={<Button kind="primary" onClick={() => setIssued(null)}>I have taken a note of it</Button>}>
          <p>Give this to <strong>{u.name}</strong>. It is shown once.</p>
          <p className="temp-password num">{issued}</p>
        </Modal>
      ) : null}
    </>
  )
}
